import { Recapt } from '../engine';
import { RequestManager } from '../managers/requestManager';
import { BaseEvent, BaseModule } from './base';
import { RecordModule } from './record';
import { EventSourceType } from './record/types';
import { Namespace } from './types';

const EVENT_SOURCE_TYPES_WHICH_IS_NOT_A_UPDATE = [
  EventSourceType.MouseMove,
  EventSourceType.MouseInteraction,
  EventSourceType.Scroll,
  EventSourceType.ViewportResize,
  EventSourceType.Input,
];

const CLICK_TAG_NAMES_WHICH_SHOULD_TRIGGER_SOMETHING = ['button', 'a'];

const CLICK_EXPECT_UPDATE_EVENT_AFTER = 3000;

/**
 * Tags considered "interactive" — when a click lands on a deep child
 * (e.g. an SVG `path` inside a sidebar `<a>`), we walk up the DOM to find
 * the nearest ancestor with one of these tags so the click is attributed to
 * the meaningful interactive element, not a generic `path` or `span`.
 */
const INTERACTIVE_TAGS = new Set([
  'A',
  'BUTTON',
  'INPUT',
  'SELECT',
  'TEXTAREA',
  'DETAILS',
  'SUMMARY',
  'LABEL',
]);

/** Maximum number of ancestors to check when bubbling up. */
const MAX_BUBBLE_DEPTH = 5;

/** Check if a single element matches the interactive criteria. */
function isInteractive(el: HTMLElement): boolean {
  if (INTERACTIVE_TAGS.has(el.tagName)) return true;
  const role = el.getAttribute?.('role');
  return (
    role === 'button' ||
    role === 'link' ||
    role === 'menuitem' ||
    role === 'tab' ||
    role === 'option' ||
    role === 'switch'
  );
}

/**
 * Walk up from the raw click target to find the nearest interactive ancestor.
 * If no ancestor is found, check immediate children and siblings for an
 * interactive element (handles Chakra UI wrappers where a `<select>` is a
 * sibling of the label/icon you actually clicked).
 *
 * Returns the interactive element if found, otherwise the original target.
 */
function resolveInteractiveTarget(
  target: HTMLElement | null,
): HTMLElement | null {
  if (!target) return null;

  try {
    // If the target itself is interactive, use it directly.
    if (isInteractive(target)) return target;

    // Walk up looking for an interactive ancestor.
    let current = target.parentElement;
    let depth = 0;
    while (current && depth < MAX_BUBBLE_DEPTH) {
      if (isInteractive(current)) return current;
      current = current.parentElement;
      depth++;
    }

    // No interactive ancestor — check siblings and direct children.
    // Handles cases like Chakra Select where <select> is a sibling of
    // the icon/label the user clicked on.
    const parent = target.parentElement;
    if (parent) {
      for (const sibling of Array.from(parent.children)) {
        if (sibling !== target && isInteractive(sibling as HTMLElement)) {
          return sibling as HTMLElement;
        }
      }
    }
  } catch {
    /* SVG elements, detached nodes, etc. — fall through */
  }

  // No interactive element found — keep the original target.
  return target;
}

interface ClickEvent extends BaseEvent {
  /** X coordinate of the click relative to the client viewport */
  x: number;
  /** Y coordinate of the click relative to the client viewport */
  y: number;
  /** Optional element information where the click occurred */
  element?: {
    /** HTML attributes of the clicked element */
    attributes?: Record<string, string>;
    tagName?: string;
    /** Visible text or aria-label of the element (truncated to 100 chars). */
    textContent?: string;
    /**
     * Position among siblings with the same tag under the parent element.
     * Only set when there are 2+ siblings with the same tag (i.e. repeated
     * elements like table rows, list items, cards). Undefined for unique
     * elements — preserving backward-compatible keys.
     */
    siblingIndex?: number;
  };
  isDead?: boolean;
  isRage?: boolean;
}

export class ClickModule extends BaseModule<ClickEvent> {
  public namespace: Namespace = 'clicks';

  private limboState: ClickEvent[] = [];
  private pollInterval: NodeJS.Timeout | null = null;

  public onClick(evt: MouseEvent) {
    if (!this.isReady) return;

    const event: ClickEvent = {
      timestamp: Recapt.getCurrentSessionTimestamp(),
      where: Recapt.where(),
      x: evt.clientX,
      y: evt.clientY,
    };

    // Resolve the element to attribute the click to.
    // Raw evt.target is often a deeply nested element (SVG path, inner span)
    // that shares the same key with dozens of siblings. We walk up the DOM
    // to find the nearest interactive ancestor (a, button, input, etc.)
    // so the click is attributed to the element the user actually intended.
    const resolvedEl = resolveInteractiveTarget(evt.target as HTMLElement);

    const parsedAttributes: Record<string, string> = {};
    let tagName: string | undefined;
    let textContent: string | undefined;

    if (resolvedEl) {
      try {
        tagName = resolvedEl.tagName;
      } catch {
        /* detached or exotic target */
      }

      try {
        if ('attributes' in resolvedEl) {
          const attributes = Array.from(
            resolvedEl.attributes as ArrayLike<unknown>,
          );
          for (const attr of attributes) {
            //@ts-expect-error
            parsedAttributes[attr.name] = attr.nodeValue;
          }
        }
      } catch {
        /* SVG, web components, etc. — attributes may not be iterable */
      }

      try {
        const raw =
          resolvedEl.textContent ||
          resolvedEl.getAttribute?.('aria-label') ||
          '';
        if (raw) textContent = raw.trim().slice(0, 100) || undefined;
      } catch {
        /* detached or exotic target */
      }
    }

    // Compute structural sibling index for element identity disambiguation.
    // This lets the analytics pipeline distinguish between repeated elements
    // (e.g. table rows, list items) that share the same CSS class.
    // The index is the element's position among siblings with the same tag.
    let siblingIndex: number | undefined;
    try {
      const parent = resolvedEl?.parentElement;
      if (parent && resolvedEl) {
        const sameTag = Array.from(parent.children).filter(
          (c) => c.tagName === resolvedEl.tagName,
        );
        if (sameTag.length > 1) {
          siblingIndex = sameTag.indexOf(resolvedEl);
        }
      }
    } catch {
      /* some targets (SVG, detached nodes, etc.) may not have parentElement */
    }

    event.element = {
      attributes: parsedAttributes,
      tagName,
      textContent,
      siblingIndex,
    };

    this.limboState.push(event);
  }

  /**
   * Binds the click event listener to the document.
   * Uses capture phase to ensure all clicks are caught.
   */
  bindClickListener() {
    document.addEventListener('click', clickListener, {
      capture: true,
    });
  }

  private rageNumFlooring = (num: number, amt?: number) =>
    Math.floor(num / (amt ?? 20)) * (amt ?? 20);

  private poll() {
    const clicks = this.limboState.slice();
    this.limboState = [];

    if (clicks.length === 0) return;

    const recordModule = Recapt.getModule<RecordModule>('record');
    if (!recordModule) {
      this.state = [...this.state, ...clicks];
      return Recapt.warn('Record module not found');
    }

    let latestEventTypes = recordModule.getLatestEventTypes();

    const rageClickMap: Record<
      string,
      {
        isRageClick: boolean;
        clicks: ClickEvent[];
      }
    > = {};

    RequestManager.potentialPainpoint();

    for (const click of clicks) {
      if (
        click.element?.tagName &&
        CLICK_TAG_NAMES_WHICH_SHOULD_TRIGGER_SOMETHING.includes(
          click.element?.tagName?.toLowerCase() ?? '',
        )
      ) {
        let anInteractionEventHappened = false;
        for (const eventType of latestEventTypes.slice()) {
          if (eventType.timestamp < click.timestamp) {
            //we shift because then the next clicks dont have to loop through as much
            latestEventTypes.shift();
            continue;
          }

          if (
            eventType.timestamp >
            click.timestamp + CLICK_EXPECT_UPDATE_EVENT_AFTER
          )
            break;

          //this event happend within 3000ms of the click (after)
          if (
            !EVENT_SOURCE_TYPES_WHICH_IS_NOT_A_UPDATE.includes(eventType.type)
          ) {
            anInteractionEventHappened = true;
            break;
          }
        }

        if (!anInteractionEventHappened) {
          Recapt.debug(
            'no interaction event happened after click, found dead click',
          );
        }

        click.isDead = !anInteractionEventHappened;
      }

      //so 20x20 px area & 2000 ms interval & 3 or more clicks = rage click
      const rageId = `${this.rageNumFlooring(click.x)}_${this.rageNumFlooring(click.y)}_${this.rageNumFlooring(click.timestamp, 2000)}_${click.where}`;

      if (rageId in rageClickMap) {
        rageClickMap[rageId].clicks.push(click);

        if (rageClickMap[rageId].clicks.length >= 3)
          rageClickMap[rageId].isRageClick = true;
      } else {
        rageClickMap[rageId] = {
          isRageClick: false,
          clicks: [click],
        };
      }
    }

    for (const rageClick of Object.values(rageClickMap)) {
      //if isRageClick is true, we can also choose to only push the first click to then only
      //render one click
      for (const click of rageClick.clicks) {
        this.state.push({ ...click, isRage: rageClick.isRageClick });
      }
    }
  }

  private startPolling() {
    if (this.pollInterval) return;
    const that = this;
    this.pollInterval = setInterval(() => {
      that.poll();
    }, RequestManager.getPollingInterval() * 3);
  }

  onStart() {
    const stored = this.loadStore<{
      state: ClickEvent[];
      limboState: ClickEvent[];
    }>();

    if (stored) {
      if ('limboState' in stored && stored.limboState.length > 0) {
        //if we have clicks in limbo, those clicks probably resulted in a hard navigation
        //so something did happen so we can just add them to the state
        this.state = [...this.state, ...stored.limboState];
      } else {
        this.state = stored.state;
      }
    }

    this.store(() => ({
      state: this.state,
      limboState: this.limboState,
    }));

    this.bindClickListener();

    this.startPolling();
  }

  onResume(): void {
    this.bindClickListener();
    this.startPolling();
  }

  onStop() {
    document.removeEventListener('click', clickListener);
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
      this.pollInterval = null;
    }
  }

  purgeState(): void {
    this.state = [];
  }

  buildRequest(
    state: ClickEvent[],
  ): Record<string, unknown> & { state: ClickEvent[] } {
    return {
      state,
    };
  }
}

const clickListener = (event: MouseEvent) => {
  try {
    Recapt.getModule<ClickModule>('clicks')?.onClick(event);
  } catch (err) {}
};
